
"use client";

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Gift, X, Loader2, Wallet, Star } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, getDocs, orderBy, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import type { WalletTransaction, PrizeWheelPrize, Coupon, User as AppUser } from '@/lib/types';
import Link from 'next/link';
import * as LucideIcons from 'lucide-react';


const prizeColors = [
    'hsl(var(--muted))',
    'hsl(var(--primary))',
    'hsl(210 20% 98%)',
    'hsl(var(--primary))',
    'hsl(var(--muted))',
    'hsl(210 20% 98%)',
    'hsl(var(--muted))',
    'hsl(var(--primary))',
];

const SPIN_COST = 2;


const PrizeModal = ({ prize, onClose }: { prize: string; onClose: () => void }) => {
    
    return (
      <div
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      >
        <Card className="relative text-center p-8 bg-card border-primary shadow-lg z-10">
          <Button variant="ghost" size="icon" className="absolute top-2 right-2" onClick={onClose}><X/></Button>
          <Gift className="h-16 w-16 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold font-headline mb-2">Parabéns!</h2>
          <p className="text-lg">Você ganhou:</p>
          <p className="text-3xl font-bold text-primary my-4">{prize}</p>
          <p className="text-sm text-muted-foreground">Mostre esta tela para resgatar seu prêmio ou copie o código do cupom.</p>
        </Card>
      </div>
    )
};

export default function PrizeWheelPage() {
  const [spinning, setSpinning] = useState(false);
  const [prizes, setPrizes] = useState<PrizeWheelPrize[]>([]);
  const [prizeIndex, setPrizeIndex] = useState<number | null>(null);
  const [targetRotation, setTargetRotation] = useState(0);
  const [showPrize, setShowPrize] = useState(false);
  const [lastWonPrize, setLastWonPrize] = useState('');
  
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [hasFreeSpin, setHasFreeSpin] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef<HTMLAudioElement>(null);
  const rotationCount = useRef(0);


  useEffect(() => {
    const fetchPrizes = async () => {
        const prizesQuery = query(collection(db, "prizeWheelPrizes"), orderBy("position"));
        const prizesSnapshot = await getDocs(prizesQuery);
        const prizesData = prizesSnapshot.docs.map(doc => doc.data() as PrizeWheelPrize);
        if (prizesData.length === 8) {
            setPrizes(prizesData);
        } else {
            console.error("Expected 8 prizes, but found", prizesData.length);
            toast({ variant: 'destructive', title: 'Erro de Configuração', description: 'A roleta de prêmios não está configurada corretamente.' });
        }
    };
    fetchPrizes();

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const unsubscribeUser = onSnapshot(userDocRef, (doc) => {
            if (doc.exists()) {
                const userData = doc.data() as AppUser;
                setAppUser(userData);
                const today = new Date().toISOString().split('T')[0];
                setHasFreeSpin(userData.lastFreeSpin !== today);
            }
        });

        const q = query(collection(db, "walletTransactions"), where("userId", "==", currentUser.uid));
        const unsubscribeWallet = onSnapshot(q, (snapshot) => {
          const balance = snapshot.docs
            .map(doc => doc.data() as WalletTransaction)
            .reduce((acc, t) => acc + t.amount, 0);
          setWalletBalance(balance);
        });

        setIsLoading(false);
        return () => {
            unsubscribeUser();
            unsubscribeWallet();
        };

      } else {
        setAppUser(null);
        setHasFreeSpin(false); // No free spin if not logged in
        setIsLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const getPrizeIndex = (): number => {
    const totalProbability = prizes.reduce((acc, prize) => acc + (prize.probability || 0), 0);
    if (totalProbability === 0) {
      return Math.floor(Math.random() * prizes.length);
    }

    let randomPoint = Math.random() * totalProbability;
    for (let i = 0; i < prizes.length; i++) {
        const prize = prizes[i];
        if (randomPoint < (prize.probability || 0)) {
            return i;
        }
        randomPoint -= (prize.probability || 0);
    }

    return prizes.length - 1;
  };

  const spinWheel = async () => {
    if (spinning || prizes.length < 8) return;
    if (!user) {
        toast({ variant: "destructive", title: "Faça login para girar!", description: "Você precisa estar logado para usar a roleta." });
        return;
    }
    
    let isPaidSpin = false;

    if (hasFreeSpin) {
        const today = new Date().toISOString().split('T')[0];
        try {
            await setDoc(doc(db, 'users', user.uid), { lastFreeSpin: today }, { merge: true });
            setHasFreeSpin(false);
        } catch (error) {
             console.error("Error updating free spin:", error);
            toast({ variant: "destructive", title: "Erro", description: "Não foi possível registrar seu giro grátis. Tente novamente." });
            return;
        }
    } else {
        if (walletBalance < SPIN_COST) {
            toast({ variant: "destructive", title: "Saldo insuficiente", description: `Você precisa de pelo menos R$ ${SPIN_COST.toFixed(2)} para girar.` });
            return;
        }
        isPaidSpin = true;
    }
    
    setSpinning(true);
    audioRef.current?.play().catch(e => console.error("Error playing audio:", e));


    if (isPaidSpin && user) {
        try {
            await addDoc(collection(db, 'walletTransactions'), {
                userId: user.uid,
                amount: -SPIN_COST,
                type: 'Compra',
                description: `Compra de giro na roleta`,
                createdAt: serverTimestamp(),
            });
        } catch (error) {
            console.error("Error debiting wallet:", error);
            toast({ variant: "destructive", title: "Erro", description: "Não foi possível debitar o valor da sua carteira." });
            setSpinning(false);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
            return;
        }
    }

    const newPrizeIndex = getPrizeIndex();
    setPrizeIndex(newPrizeIndex);
    
    const segmentAngle = 360 / prizes.length;
    const prizeRotation = 360 - newPrizeIndex * segmentAngle - segmentAngle / 2;
    
    rotationCount.current += 1;
    const fullRotations = rotationCount.current * 5 * 360; 
    setTargetRotation(fullRotations + prizeRotation);

    setTimeout(async () => {
      const prize = prizes[newPrizeIndex];
      let wonPrizeDisplay = prize.label;

      if (prize.type === 'coupon' && user) {
          const couponCode = `ROLETAVIP-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
          const expiresAt = new Date();
          expiresAt.setHours(expiresAt.getHours() + 24);

          const couponData: Omit<Coupon, 'id'> = {
              code: couponCode,
              type: 'Porcentagem',
              value: Number(prize.value) || 0,
              usageLimit: 1,
              used: 0,
              status: 'Ativo',
              userId: user.uid,
              firstTimeOnly: false,
              onePerUser: true,
              expiresAt: Timestamp.fromDate(expiresAt),
          };

          try {
              await addDoc(collection(db, "coupons"), couponData);
              wonPrizeDisplay = `Cupom: ${couponCode}`;
               toast({
                  title: "Você ganhou um cupom!",
                  description: `Use o código ${couponCode} na sua próxima compra. Válido por 24 horas.`,
                  duration: 10000,
              });
          } catch (error) {
               console.error("Error creating coupon:", error);
               wonPrizeDisplay = "Erro ao gerar cupom";
          }
      } else {
           if (prize.type === 'text' && prize.label === 'Tente Novamente') {
               toast({
                  title: "Não foi dessa vez!",
                  description: "Você pode tentar novamente comprando outro giro.",
              });
           }
      }
      
      setLastWonPrize(wonPrizeDisplay);
      setSpinning(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      if (prize.type !== 'text' || prize.label !== 'Tente Novamente') {
        setShowPrize(true);
      }
      
    }, 7000);
  };

  const renderSpinButton = () => {
      if (spinning) {
          return <Button size="lg" disabled><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Girando...</Button>
      }
      if (!user) {
         return <Button size="lg" asChild><Link href="/login?redirect=/roletadepremios">Faça Login para Girar</Link></Button>
      }
      if (hasFreeSpin) {
          return <Button size="lg" onClick={spinWheel}>Girar a Roleta! (Grátis)</Button>
      }
      return (
          <Button size="lg" onClick={spinWheel} disabled={walletBalance < SPIN_COST}>
              <Wallet className="mr-2 h-4 w-4" />
              Girar por R$ ${SPIN_COST.toFixed(2)}
          </Button>
      )
  }

  const conicGradient = `conic-gradient(${prizeColors.map((color, i) => `${color} ${i * (100 / prizes.length)}%, ${color} ${(i + 1) * (100 / prizes.length)}%`).join(', ')})`;

  if (prizes.length === 0 && !isLoading) {
     return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4">
             <h1 className="text-4xl md:text-5xl font-bold font-headline text-primary mb-2 text-center">Roleta Indisponível</h1>
             <p className="text-muted-foreground mb-8 text-center">A roleta de prêmios está sendo configurada. Volte mais tarde!</p>
        </div>
     )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 overflow-hidden">
      <audio ref={audioRef} src="/sounds/wheel-spin.mp3" loop />
      
      <h1 className="text-4xl md:text-5xl font-bold font-headline text-primary mb-2 text-center">Roleta de Prêmios</h1>
      <p className="text-muted-foreground mb-4 text-center">Gire a roleta e ganhe prêmios exclusivos!</p>
      
      <div className="text-center mb-8 space-y-2">
        {user && (
            <div className="text-lg flex items-center gap-2 font-semibold">
                <Wallet className="h-5 w-5 text-primary" />
                <span>Seu Saldo:</span>
                <span className="text-primary">R$ ${walletBalance.toFixed(2)}</span>
            </div>
        )}
        {user && (
            <div className="text-md flex items-center justify-center gap-2 font-semibold">
                <Star className="h-5 w-5 text-yellow-400" />
                <span>
                    {hasFreeSpin ? 'Você tem 1 Giro Grátis Disponível' : 'Giro grátis de hoje já foi usado'}
                </span>
            </div>
        )}
      </div>
      
      <div className="relative w-80 h-80 md:w-96 md:h-96 flex items-center justify-center mb-8">
        <div className="absolute -top-2 z-20" style={{ filter: 'drop-shadow(0px 5px 5px rgba(0,0,0,0.5))' }}>
          <div className="w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-t-[30px] border-t-primary" />
        </div>
        <div
          className="relative w-full h-full rounded-full border-8 border-primary shadow-2xl overflow-hidden"
          style={{ 
            background: conicGradient,
            transform: `rotate(${targetRotation}deg)`,
            transition: spinning ? 'transform 7s cubic-bezier(0.1, 0.5, 0.2, 1)' : 'none',
          }}
        >
         {prizes.map((prize, i) => {
            const segmentAngle = 360 / prizes.length;
            const angle = i * segmentAngle + (segmentAngle / 2);
            
            const textColor = prizeColors[i] === 'hsl(210 20% 98%)' 
              ? 'hsl(var(--primary-foreground))'
              : 'hsl(var(--secondary-foreground))';

            return (
              <div
                key={i}
                className="absolute top-0 left-0 w-full h-full flex justify-center items-start"
                style={{
                  transform: `rotate(${angle}deg)`,
                }}
              >
                <div
                    className="flex flex-col items-center"
                    style={{
                      transform: `translateY(30px) rotate(-${angle}deg)`,
                      textAlign: 'center',
                      color: textColor
                    }}
                  >
                   <span className="font-bold text-3xl md:text-4xl">{i + 1}</span>
                </div>
              </div>
            )
          })}
        </div>
        <div className="absolute w-20 h-20 bg-card rounded-full border-4 border-primary z-10 flex items-center justify-center"/>
      </div>

       {isLoading ? <Loader2 className="animate-spin h-8 w-8" /> : renderSpinButton()}

       <Card className="mt-8 w-full max-w-lg">
            <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-center mb-2 font-headline">Prêmios Disponíveis</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-muted-foreground">
                    {prizes.map((p, i) => {
                      const Icon = (LucideIcons as any)[p.iconName] || Gift;
                      let label = p.label;
                       if (p.type === 'coupon') {
                         label = `${p.value}% de Desconto`;
                       }
                      return (
                         <li key={`prize-list-${i}`} className="flex items-center gap-2">
                            <span className="font-bold text-primary">{i + 1}:</span>
                            <Icon className="w-4 h-4 text-primary" />
                            {label}
                        </li>
                      )
                    })}
                </ul>
            </CardContent>
       </Card>

      {showPrize && prizeIndex !== null && (
        <PrizeModal 
          prize={lastWonPrize} 
          onClose={() => setShowPrize(false)} 
        />
      )}
    </div>
  );
}

    